import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Alert, Button, Spinner } from "../../design-system";
import { Icon } from "../../design-system";
import { AuthShell } from "../../design-system/layout/AuthShell";
import {
  verifyRegistration,
  resetRegisterFlag,
  resendRegistrationVerificationCode,
} from "../../slices/thunks";

const RESEND_COOLDOWN_SEC = 60;

const selectState = createSelector(
  (state) => state.Account,
  (account) => ({
    loading: account.loading,
    error: account.registrationError,
  }),
);

const VerifyRegistration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const email = useMemo(() => {
    const fromState = location.state?.email;
    const fromStorage = sessionStorage.getItem("kronaly_register_email");
    const raw = (fromState || fromStorage || "").trim();
    return raw.toLowerCase();
  }, [location.state?.email]);

  const [digits, setDigits] = useState(["", "", "", ""]);
  const inputsRef = useRef([]);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(null);
  const [resendError, setResendError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { loading, error } = useSelector(selectState);

  useEffect(() => {
    document.title = "Verificar correo | Kronaly";
    dispatch(resetRegisterFlag());
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.email) {
      sessionStorage.setItem(
        "kronaly_register_email",
        String(location.state.email).trim().toLowerCase(),
      );
    }
  }, [location.state?.email]);

  useEffect(() => {
    if (!email) navigate("/register", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const setDigit = (index, value) => {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < 3) inputsRef.current[index + 1]?.focus();
  };

  const onKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const code = digits.join("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 4 || !email) return;
    try {
      await dispatch(verifyRegistration({ email, code }, navigate));
    } catch (_) {
      /* mensaje en redux */
    }
  };

  const onResend = useCallback(async () => {
    if (!email || resending || resendCooldown > 0) return;
    setResendError(null);
    setResendSuccess(null);
    setResending(true);
    try {
      const result = await dispatch(resendRegistrationVerificationCode(email));
      setResendSuccess(result.message);
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setResendError(typeof err === "string" ? err : err?.message || "No se pudo reenviar el código");
    } finally {
      setResending(false);
    }
  }, [dispatch, email, resendCooldown, resending]);

  if (!email) return null;

  return (
    <AuthShell
      footer={
        <span>
          ¿Email incorrecto?{" "}
          <Link to="/register" style={{ color: "var(--kr-fg-link)", fontWeight: 600 }}>
            Volver al registro
          </Link>
        </span>
      }
    >
      <div className="kr-text-center kr-mb-4">
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: "var(--kr-action-soft-bg)",
            color: "var(--kr-action-soft-fg)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <Icon name="Mail" size={24} />
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
          Verifica tu correo
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Hemos enviado un código de 4 dígitos a
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
          {email}
        </p>
      </div>

      {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}
      {resendSuccess && <div className="kr-mb-3"><Alert tone="success">{resendSuccess}</Alert></div>}
      {resendError && <div className="kr-mb-3"><Alert tone="warning">{resendError}</Alert></div>}

      <form onSubmit={onSubmit} className="kr-flex kr-col kr-gap-4">
        <div className="kr-flex kr-justify-center kr-gap-2">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              autoFocus={i === 0}
              style={{
                width: 56,
                height: 64,
                fontSize: 28,
                fontWeight: 600,
                textAlign: "center",
                fontFamily: "var(--kr-font-mono)",
                fontVariantNumeric: "tabular-nums",
                color: "var(--kr-fg-strong)",
                border: "1px solid var(--kr-border)",
                borderRadius: "var(--kr-radius-md)",
                background: "var(--kr-bg-surface)",
                outline: "none",
                transition: "border-color var(--kr-dur-fast)",
              }}
              onFocus={(e) => { e.target.style.borderColor = "var(--kr-action)"; e.target.style.boxShadow = "var(--kr-shadow-focus)"; }}
              onBlur={(e) => { e.target.style.borderColor = "var(--kr-border)"; e.target.style.boxShadow = "none"; }}
            />
          ))}
        </div>

        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={loading || code.length !== 4}
          fullWidth
        >
          {loading && <Spinner size={14} color="#fff" />}
          {loading ? "Verificando…" : "Verificar"}
        </Button>

        <div className="kr-text-center kr-fs-12 kr-text-muted">
          ¿No recibiste el código?{" "}
          <button
            type="button"
            onClick={onResend}
            disabled={resending || resendCooldown > 0}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: resendCooldown > 0 || resending ? "var(--kr-fg-subtle)" : "var(--kr-fg-link)",
              fontWeight: 600,
              cursor: resendCooldown > 0 || resending ? "not-allowed" : "pointer",
            }}
          >
            {resending ? "Reenviando…" : resendCooldown > 0 ? `Reenviar (${resendCooldown}s)` : "Reenviar código"}
          </button>
        </div>
      </form>
    </AuthShell>
  );
};

export default VerifyRegistration;
